import { Box, Card, Typography, Divider } from '@mui/material';

const SectionFormGuidelines = () => {
    return (
        <Card sx={{ p: { xs: 2, md: 4 } , maxHeight:'74vh', overflowY: 'auto' }}>
            {/* <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>Section Form Guidelines</Typography> */}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Basic Information</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Title *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The main heading/title for this section. This will be displayed prominently at the top of the section in the app.
                            Use a clear, concise title that describes what content the section contains.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Subtitle *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A secondary line of text that appears below the title. Use this to provide additional context or a brief description
                            of the section's content. Keep it short and informative.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Description *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A detailed description of the section. This field supports multi-line text and is used to provide comprehensive
                            information about the section's purpose or content. The description visibility can be controlled via Display Options.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Display Options</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Collapsible section that controls which text elements are visible and their alignment positions.
                    </Typography>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Title</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the section title. When enabled, you can select title alignment (Left/Center/Right).
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Subtitle</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the section subtitle. When enabled, you can select subtitle alignment (Left/Center/Right).
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Description</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the section description. When enabled, you can select description alignment (Left/Center/Right).
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Section Type *</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Defines how items within this section are displayed. Choose the layout that best fits your content:
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Horizontal Scroll</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items displayed in a horizontal row that can be scrolled left/right. Great for featured items or promotions.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Carousel</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Auto-sliding banner style display. Best for promotional banners or hero content with auto-scroll duration setting.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Grid 2 Column</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items displayed in a 2-column grid layout. Ideal for medium-sized content cards.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Grid 3 Column</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items displayed in a 3-column grid. Good for compact item listings or categories.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Grid 4 Column</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items displayed in a 4-column grid. Perfect for displaying many small items like icons or quick actions.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Fixed Banner</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Single fixed image/banner that doesn't scroll. Typically used for important announcements or advertisements.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Vertical List</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Items displayed in a vertical stacked list. Best for news feeds, blog posts, or sequential content.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Mixed Grid</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Combination of different layouts within the same section. Allows for varied item sizes and arrangements.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Background Settings</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Background Type</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Determines the background style for the entire section:
                        </Typography>
                        <Box sx={{ mt: 1, pl: 2 }}>
                            <Typography variant="body2">• <strong>None:</strong> Transparent background, inherits from parent</Typography>
                            <Typography variant="body2">• <strong>Color:</strong> Solid single color background</Typography>
                            <Typography variant="body2">• <strong>Image:</strong> Full background image (uploaded separately)</Typography>
                            <Typography variant="body2">• <strong>Gradient:</strong> Color gradient background</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Background Color</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Appears when Background Type is set to "Color". Use the color picker or enter a hex code (e.g., #FFFFFF).
                            This sets the solid background color behind all content in the section.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Background Image</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Appears when Background Type is set to "Image". Upload a high-quality image that will serve as the section's background.
                            The image will be scaled to cover the entire section area.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>View All Button</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Show View All</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to display a "View All" button/link at the bottom of the section. This allows users to navigate
                            to a full page/list of items in this section.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>View All Text *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The label text for the View All button. Common examples: "View All", "See More", "Browse All", "View All Products".
                            Required when Show View All is enabled.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>View All Route</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The app navigation route/path to navigate to when the View All button is tapped.
                            Example: "/products", "/category/electronics", "/promotions"
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Scheduling</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Start Date/Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The date and time when this section becomes visible and active in the app. Leave empty to show immediately.
                            Use this to schedule promotional sections in advance. The section will automatically appear when the start time is reached.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>End Date/Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The date and time when this section expires and becomes hidden from the app. Leave empty for no expiration.
                            Use this for time-limited promotions, seasonal content, or flash sales. After this time, the section will be hidden.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Section-Specific Settings</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Auto Scroll Duration (sec)</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Available only when Section Type is set to "Carousel". Controls how long each slide is displayed before
                            automatically advancing to the next slide. Enter duration in seconds (e.g., 3 for 3 seconds per slide).
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Max Items To Show</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Limits the number of items displayed within this section, regardless of how many items are actually assigned.
                            Use this to control section length, improve performance, or create teaser previews. Leave empty to show all items.
                        </Typography>
                    </Box>
                </Box>

            </Box>
        </Card>
    );
};

export default SectionFormGuidelines;
